import { type RefObject, useEffect, useRef, useState } from "react";

// Count of currently-open dropdowns/menus. A document-level key handler
// (Companion's Esc-Esc shortcut) reads this synchronously at keydown time to
// tell whether an Escape belongs to an open dropdown before this hook's own
// listener closes it — so dismissing a dropdown never counts toward sign-out.
let openDropdownCount = 0;

/** How many dropdowns (via useDropdownState) are currently open. */
export const getOpenDropdownCount = (): number => openDropdownCount;

export interface UseDropdownStateOptions {
  /** Fires whenever isOpen transitions, with the new value. */
  onOpenChange?: (isOpen: boolean) => void;
}

export interface UseDropdownStateReturn<TTrigger extends HTMLElement> {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  /** Attach to the trigger element. Used for click-outside detection
   *  and trigger-rect measurement. */
  triggerRef: RefObject<TTrigger | null>;
  /** Attach to the menu element if you portal it. Used for
   *  click-outside detection so clicks inside the menu don't close. */
  menuRef: RefObject<HTMLDivElement | null>;
  /** Live `DOMRect` of the trigger while the menu is open. Recomputed
   *  on scroll + resize. `null` when closed. */
  triggerRect: DOMRect | null;
}

/**
 * Shared open/close + outside-click + position-tracking state for
 * portal-rendered dropdowns and menus.
 */
export function useDropdownState<
  TTrigger extends HTMLElement = HTMLButtonElement,
>({
  onOpenChange,
}: UseDropdownStateOptions = {}): UseDropdownStateReturn<TTrigger> {
  const [isOpen, setIsOpen] = useState(false);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<TTrigger | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;

  // Fire onOpenChange whenever isOpen changes, matching the
  // pre-refactor DSDropdown behaviour (also fires once on mount).
  useEffect(() => {
    onOpenChangeRef.current?.(isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setTriggerRect(null);
      return;
    }
    const update = () => {
      if (triggerRef.current) {
        setTriggerRect(triggerRef.current.getBoundingClientRect());
      }
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const inTrigger = triggerRef.current?.contains(target) ?? false;
      const inMenu = menuRef.current?.contains(target) ?? false;
      if (!inTrigger && !inMenu) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    openDropdownCount++;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => {
      document.removeEventListener("keydown", handler);
      openDropdownCount--;
    };
  }, [isOpen]);

  return { isOpen, setIsOpen, triggerRef, menuRef, triggerRect };
}
